"use client";

import { useEffect, useRef } from "react";
import { Globe, Search, Target, ChartBar as BarChart3, ShoppingBag, Code, FileText, BrainCircuit } from 'lucide-react';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
const rituals = [{
  icon: Globe,
  number: "01",
  title: "National SEO",
  description: "I build authority for your brand across the UK, ensuring you rank for high-intent keywords that drive qualified traffic and sustainable growth.",
  meta: "Strategic SEO Growth"
}, {
  icon: Target,
  number: "02",
  title: "International SEO",
  description: "I expand your reach into global markets with precise technical implementation and content strategies tailored to specific regions and languages.",
  meta: "Global Market Reach"
}, {
  icon: Search,
  number: "03",
  title: "Local SEO",
  description: "I help you dominate local search results, ensuring your business is the first choice for customers searching in your specific service areas.",
  meta: "Local Visibility Focus"
}, {
  icon: BarChart3,
  number: "04",
  title: "B2B SEO & Lead Gen",
  description: "I focus on high-value B2B search terms to attract decision-makers, turning your website into a consistent engine for high-quality business leads.",
  meta: "B2B Lead Generation"
}, {
  icon: ShoppingBag,
  number: "05",
  title: "Ecommerce SEO",
  description: "I optimise your product architecture and category pages to increase organic sales, improve conversion rates, and maximise your online revenue.",
  meta: "Ecommerce Sales Growth"
}, {
  icon: Code,
  number: "06",
  title: "Technical SEO",
  description: "I resolve complex crawl issues, site speed bottlenecks, and indexing errors to ensure search engines can perfectly interpret your website content.",
  meta: "Technical Performance"
}, {
  icon: FileText,
  number: "07",
  title: "SEO Audits",
  description: "I provide a deep-dive analysis of your current performance, identifying clear, actionable opportunities to improve your search engine rankings.",
  meta: "Actionable SEO Insights"
}, {
  icon: BrainCircuit,
  number: "08",
  title: "AI Search Optimisation",
  description: "I prepare your brand for the future of search, optimising for AI-powered answers and emerging search experiences across modern platforms.",
  meta: "Future-Proof Search"
}];
export default function Services() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const cardsRef = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (!sectionRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add({
        reduced: "(prefers-reduced-motion: reduce)",
        normal: "(prefers-reduced-motion: no-preference)"
      }, context => {
        const conditions = context.conditions as {
          reduced: boolean;
          normal: boolean;
        };
        const validCards = cardsRef.current.filter((el): el is HTMLDivElement => el !== null);
        if (conditions.reduced) {
          gsap.set(validCards, {
            opacity: 1,
            y: 0
          });
          return;
        }
        gsap.from(validCards, {
          opacity: 0,
          y: 32,
          duration: 0.72,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
            once: true
          }
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return <section data-section-id="23037" ref={sectionRef} id="industrysectorsserved" className="bg-background text-foreground py-32 md:py-48 px-6 md:px-12 lg:px-20">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-8 mb-24 md:mb-32">
          <div className="md:col-span-2">
            <span className="font-sans uppercase text-xs font-medium text-muted-foreground" style={{
            letterSpacing: "0.16em"
          }}>
              MY SERVICES
            </span>
          </div>
          <div className="md:col-span-10">
            <h2 className="font-serif text-foreground" style={{
            fontWeight: 600,
            fontSize: "clamp(1.8125rem, 2.8vw, 2.375rem)",
            letterSpacing: "-0.028em",
            lineHeight: 0.96
          }}>
              {"Tailored SEO strategies "}
              <span className="relative inline-block italic text-primary" style={{
              fontWeight: 400
            }}>
                for growth
                <svg className="absolute left-0 -bottom-2 w-full" viewBox="0 0 200 14" preserveAspectRatio="none" aria-hidden="true" style={{
                height: "0.35em"
              }}>
                  <path d="M2 8 Q 50 2, 100 6 T 198 5" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
                </svg>
              </span>
              {"."}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
          {rituals.map((ritual, i) => {
          const Icon = ritual.icon;
          return <div key={i} data-index={i} ref={el => {
            cardsRef.current[i] = el;
          }} className="group bg-card rounded-lg p-8 md:p-10 lg:p-12 flex flex-col min-h-[380px] transition-colors duration-300 hover:bg-muted border border-border">
                <div className="mb-8">
                  <Icon className="transition-transform duration-300 group-hover:translate-x-1 text-primary" style={{
                width: 32,
                height: 32,
                strokeWidth: 1.5
              }} aria-hidden="true" />
                </div>

                <div className="font-sans uppercase font-medium text-muted-foreground mb-4" style={{
              fontSize: "14px",
              letterSpacing: "0.16em"
            }}>
                  SERVICE - {ritual.number}
                </div>

                <h3 className="font-serif text-foreground mb-5" style={{
              fontWeight: 500,
              fontSize: "clamp(1.375rem, 1.75vw, 1.625rem)",
              letterSpacing: "-0.02em",
              lineHeight: 1.05
            }}>
                  {ritual.title}
                </h3>

                <p className="font-sans text-muted-foreground mb-10 flex-grow" style={{
              fontSize: "1.0625rem",
              lineHeight: 1.6
            }}>
                  {ritual.description}
                </p>

                <div className="flex items-center justify-between gap-4 mt-auto">
                  <span className="font-sans text-foreground/70" style={{
                fontSize: "clamp(0.875rem, 0.95vw, 0.9375rem)",
                letterSpacing: "0.02em"
              }}>
                    {ritual.meta}
                  </span>
                  <Button variant="outline" className="rounded-md border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground px-6 py-2 h-auto font-sans font-medium transition-all duration-300" style={{
                letterSpacing: "0.01em"
              }}>
                    Learn More
                  </Button>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}